import { useState } from 'react'
import { User } from '../../types'
import { hasPermission } from '../../lib/permissions'
import { runArSmsCadence, ArSmsRunSummary, ArSmsOutcome } from '../../services/arSmsCadence'
import './ArSmsPanel.css'

/**
 * AR SMS reminders — manual only.
 *
 * Texting a customer about money they owe is an outbound act, so nothing here
 * fires on its own: the operator asks what would go out, reads the list, and
 * confirms. Same shape as the PPSR lodgement panel.
 *
 * The dry run and the real run are the same code path in arSmsCadence, so the
 * preview cannot drift from what actually gets sent.
 */

const STAGE_LABEL: Record<string, string> = {
  due_soon: 'Due tomorrow',
  overdue_1: 'Overdue 1–2 days',
  overdue_3: 'Overdue 3–6 days',
  overdue_7: 'Overdue 7+ days',
}

const SKIP_LABEL: Record<string, string> = {
  skipped_already_sent: 'already sent for this stage',
  skipped_no_stage: 'not at a reminder stage',
  skipped_no_mobile: 'no buyer mobile on file',
  skipped_paid: 'paid or closed',
}

export default function ArSmsPanel({ user }: { user: User }) {
  const [busy, setBusy] = useState(false)
  const [preview, setPreview] = useState<ArSmsRunSummary | null>(null)
  const [result, setResult] = useState<ArSmsRunSummary | null>(null)
  const [error, setError] = useState<string | null>(null)

  if (!hasPermission(user, 'perm-manage-collections')) return null

  const check = async () => {
    setBusy(true)
    setError(null)
    setResult(null)
    try {
      setPreview(await runArSmsCadence(undefined, { dryRun: true }))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not check SMS reminders')
    } finally {
      setBusy(false)
    }
  }

  const send = async () => {
    setBusy(true)
    setError(null)
    try {
      const summary = await runArSmsCadence()
      setPreview(null)
      setResult(summary)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send SMS reminders')
    } finally {
      setBusy(false)
    }
  }

  const due: ArSmsOutcome[] = preview ? preview.outcomes.filter((o) => o.action === 'would_send') : []

  const skipSummary = (summary: ArSmsRunSummary) => {
    const counts: Record<string, number> = {}
    summary.outcomes
      .filter((o) => o.action.indexOf('skipped') === 0)
      .forEach((o) => {
        counts[o.action] = (counts[o.action] || 0) + 1
      })
    const parts = Object.keys(counts).map((k) => `${counts[k]} ${SKIP_LABEL[k] || k}`)
    return parts.length ? parts.join(', ') : 'none'
  }

  return (
    <div className="arsms-panel">
      <div className="arsms-head">
        <span className="arsms-title">SMS reminders</span>
        <span className="arsms-sub">
          Text the buyer alongside the email ladder. Nothing sends until you confirm.
        </span>
      </div>

      <div className="arsms-actions">
        <button className="arsms-btn arsms-btn-primary" disabled={busy} onClick={check}>
          {busy ? 'Working…' : 'Check SMS reminders'}
        </button>
      </div>

      {error ? <div className="arsms-alert error">{error}</div> : null}

      {result ? (
        <div className="arsms-alert">
          Sent {result.sent}, failed {result.failed}, skipped {result.skipped} of {result.checked}{' '}
          invoice{result.checked === 1 ? '' : 's'} checked. Skipped: {skipSummary(result)}.
          {result.failed > 0 ? ' Failures are recorded in sms_log and will retry on the next run.' : ''}
        </div>
      ) : null}

      {preview ? (
        <div className="arsms-overlay" onClick={() => setPreview(null)}>
          <div className="arsms-modal" onClick={(e) => e.stopPropagation()}>
            <h4>Send {due.length} SMS reminder{due.length === 1 ? '' : 's'}?</h4>

            {due.length === 0 ? (
              <p>
                Nothing is due for an SMS right now. Checked {preview.checked} invoice
                {preview.checked === 1 ? '' : 's'} — skipped: {skipSummary(preview)}.
              </p>
            ) : (
              <>
                <p>
                  These buyers will receive a text message about money owed. Checked{' '}
                  {preview.checked} invoice{preview.checked === 1 ? '' : 's'}; skipped:{' '}
                  {skipSummary(preview)}.
                </p>
                <ul className="arsms-list">
                  {due.map((o) => (
                    <li key={o.invoiceId}>
                      <strong>{o.invoiceNumber || o.invoiceId.slice(0, 8)}</strong>{' '}
                      <span className="arsms-stage">{STAGE_LABEL[o.stage || ''] || o.stage}</span>{' '}
                      <span className="arsms-to">{o.to}</span>
                    </li>
                  ))}
                </ul>
                <p className="arsms-check">Are you sure you would like to proceed?</p>
              </>
            )}

            <div className="arsms-modal-actions">
              <button className="arsms-btn" onClick={() => setPreview(null)}>
                {due.length === 0 ? 'Close' : 'Cancel'}
              </button>
              {due.length > 0 ? (
                <button className="arsms-btn arsms-btn-primary" disabled={busy} onClick={send}>
                  {busy ? 'Sending…' : `Yes, send ${due.length}`}
                </button>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}
